// src/pages/SuperAdminManagement.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  fetchAdmins, fetchSuperAdmins, deleteUser, fetchUserDetails,
} from '../services/apiService';
import UserTypeList from '../components/UserTypeList';
import menuItemsByService from '../categories/serviceMenus';
import { useSidebar } from '../context/SidebarContext';

const SuperAdminManagement = () => {
  const { authTokens, impersonatedUserId } = useContext(AuthContext);
  const [admins, setAdmins] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const { isSidebarOpen } = useSidebar();
  const [userServices, setUserServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      const fetchedAdmins = await fetchAdmins(authTokens);
      const fetchedSuperAdmins = await fetchSuperAdmins(authTokens);
      setAdmins(fetchedAdmins);
      setSuperAdmins(fetchedSuperAdmins);
    };

    loadData();
  }, [authTokens]);

  useEffect(() => {
    const loadUserServices = async () => {
      if (authTokens && (impersonatedUserId || authTokens.userRole !== 'admin')) {
        try {
          const userIdToFetch = impersonatedUserId || authTokens.userId;
          const userDetails = await fetchUserDetails(userIdToFetch, authTokens);
          const serviceOrder = {
            'Anexo 30 y 31 2022': 1,
            'Anexo 30 y 31 2023': 2,
            'Anexo 30 y 31 2024': 3,
            'Anexo 30 y 31 2025': 4,
            'Dictamen de Diseño': 5,
            'NOM-016-CRE-2016': 6,
            'Anexo 30 y 31 para transporte o distribución': 7,
          };
          const sortedServices = userDetails.services.sort(
            (a, b) => (serviceOrder[a.name] || 0) - (serviceOrder[b.name] || 0),
          );
          setUserServices(sortedServices);
        } catch (error) {
          console.error('Error al cargar los servicios del usuario:', error);
        }
      }
    };

    loadUserServices();
  }, [authTokens, impersonatedUserId]);

  const handleEdit = (userId) => {
    navigate(`/edit-user/${userId}`);
  };

  const handleDelete = async (userId, userType) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        await deleteUser(userId, authTokens);
        // Actualiza el estado para reflejar la eliminación
        if (userType === 'admin') {
          setAdmins(admins.filter((admin) => admin.id !== userId));
        } else if (userType === 'superAdmin') {
          setSuperAdmins(superAdmins.filter((superAdmin) => superAdmin.id !== userId));
        }
      } catch (error) {
        alert(error.message || 'Error al eliminar el usuario');
      }
    }
  };

  if (!authTokens) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`fixed md:fixed top-16 left-0 h-[calc(100vh-4rem)] overflow-y-auto transform
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          transition duration-200 ease-in-out
          w-64 bg-white shadow-lg z-40`}
      >
        <div className="p-8 border-b">
          <img src="/images/logo2.png" alt="Logo Petrotec" className="h-32 w-auto mx-auto" />
        </div>
        <nav className="mt-6">
          <Link
            to="/dashboard"
            className={`
              block px-4 py-3 hover:bg-blue-50 transition-colors duration-200
              text-gray-700 hover:text-blue-600 border-b border-gray-100
            `}
          >
            Inicio
          </Link>
          <div className="px-4 py-2">
            <h2 className="text-xs uppercase font-semibold text-gray-600 tracking-wide">
              Servicios Disponibles
            </h2>
          </div>
          {userServices.map((service) => {
            const firstMenuItem = menuItemsByService[service.name]?.[0];
            return (
              <Link
                key={service.id}
                to={firstMenuItem?.path || '#'}
                className="block px-4 py-3 hover:bg-blue-50 transition-colors duration-200 text-gray-700 hover:text-blue-600"
              >
                {service.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-200 ${isSidebarOpen ? 'md:ml-64' : ''}`}>

        <div className="p-8 h-[calc(100vh-4rem)]">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Gestión de Usuarios</h1>
            <p className="mt-2 text-gray-600">
              Administra inspectores y gerentes del sistema.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100%-6rem)]">
            <div className="bg-white shadow-lg rounded-lg p-6 h-full">
              <UserTypeList
                title="Inspectores"
                users={admins}
                onEdit={(userId) => handleEdit(userId)}
                onDelete={(userId) => handleDelete(userId, 'admin')}
              />
            </div>

            <div className="bg-white shadow-lg rounded-lg p-6 h-full">
              <UserTypeList
                title="Gerentes"
                users={superAdmins}
                onEdit={(userId) => handleEdit(userId)}
                onDelete={(userId) => handleDelete(userId, 'superAdmin')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminManagement;
